import { CloseOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import VersionPreviewModal from '@zeus/core/modals/VersionPreviewModal';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import ReadmeHistory from '_/common/components/readme/ReadmeHistory';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { ORGANIZATION_QUERY_KEY } from '_/hooks/useOrganizations';
import zeusSdk from '_/utils/SDKs';
import { notification } from 'antd';
import React, { ReactElement, useCallback, useState, useMemo, useEffect } from 'react';

const OrgReadmeHistory = (): ReactElement => {
  const queryClient = useQueryClient();
  const history = useNavigate();
  const { selectedOrganization: org } = useOrganization();
  const { id: selectedOrgId, slug: selectedOrgSlug } = org || {};
  const { setBreadcrumbs, setActionButtons } = useTopHeader();

  const [selectedVersion, setSelectedVersion] = useState({});
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  // Fetch readme versions using React Query
  const { data: rawReadmeVersions, error: fetchError } = useQuery({
    queryKey: ['orgReadmeVersions', selectedOrgId],
    queryFn: async () => {
      if (!selectedOrgId) return { items: [], metadata: {} };
      return zeusSdk.flows.getChild(selectedOrgId, 'readme/_versions');
    },
    enabled: !!selectedOrgId,
  });

  // Handle fetch errors
  useEffect(() => {
    if (fetchError) {
      notification.error({
        message: 'Error fetching readme versions',
      });
      console.error('Error fetching readme versions for organization', fetchError);
    }
  }, [fetchError]);

  // Adapt versions data to ReadmeHistory format
  const readmeVersions = useMemo(() => readmeHistoryAdapter(rawReadmeVersions?.items || []), [rawReadmeVersions]);

  // Mutation for updating org readme
  const { mutate: updateReadmeMutate } = useMutation({
    mutationFn: async ({ orgId, content }: { orgId: string; content: string }) => {
      const result = await zeusSdk.orgs.updateChild(orgId, 'readme', { content: content });
      return result;
    },
    onSuccess: () => {
      notification.success({
        message: 'Organization Readme Saved',
      });
      // Invalidate organization query to refetch with updated readme
      if (selectedOrgSlug) {
        void queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY(selectedOrgSlug) });
      }
      // Invalidate readme versions query
      void queryClient.invalidateQueries({ queryKey: ['orgReadmeVersions', selectedOrgId] });
    },
    onError: (error: any) => {
      console.error('Error updating organization', error);
      notification.error({
        message: 'Error updating readme',
      });
    },
  });

  const handleReturnToOrg = useCallback(() => {
    void history({ to: `/orgs/${selectedOrgSlug}/` });
  }, [history, selectedOrgSlug]);

  const restoreToPreviousVersion = useCallback(
    (version) => {
      if (!selectedOrgId) return;

      const restoredReadme = version.after.content;
      updateReadmeMutate({ orgId: selectedOrgId, content: restoredReadme });
      handleReturnToOrg();

      if (previewModalVisible) {
        setPreviewModalVisible(false);
        setSelectedVersion({});
      }
    },
    [updateReadmeMutate, handleReturnToOrg, previewModalVisible, selectedOrgId],
  );

  const previewPreviousVersion = useCallback(
    (version) => {
      setSelectedVersion(version);
      setPreviewModalVisible(!previewModalVisible);
    },
    [previewModalVisible],
  );

  const exitButton = useMemo(
    () => ({
      onClick: handleReturnToOrg,
      icon: <CloseOutlined />,
      label: 'Close',
      tooltipTitle: 'Return to Organization',
    }),
    [handleReturnToOrg],
  );

  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: org?.name || 'Organization', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Readme History' },
    ]);
    setActionButtons([exitButton]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [exitButton, setActionButtons, setBreadcrumbs, org?.name, selectedOrgSlug]);

  return (
    <>
      <ReadmeHistory
        readmeVersions={readmeVersions}
        onRestoreClick={restoreToPreviousVersion}
        onPreviewClick={previewPreviousVersion}
      />
      <VersionPreviewModal
        open={previewModalVisible}
        versionData={selectedVersion}
        onRestoreClick={restoreToPreviousVersion}
        onCancelClick={previewPreviousVersion}
      />
    </>
  );
};

export default OrgReadmeHistory;
